import { Capacitor, registerPlugin } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { captureError } from 'common/utils/sentry';

// Native side: ios/App/App/MetaAttributionPlugin.swift and
// android/app/src/main/java/com/dr/doqit/MetaAttributionPlugin.java.
//
// Why this exists: RevenueCat's Meta Ads integration will not send an App
// Store event to Meta unless the customer has a Facebook anonymous ID or an
// ATT-authorised IDFA. Until 2026-09-03 the app had neither, so every App
// Store trial start and purchase was silently dropped and Meta only ever saw
// web subscriptions. These two helpers close that gap.

export type TrackingAuthorizationStatus =
  | 'authorized'
  | 'denied'
  | 'restricted'
  | 'notDetermined'
  | 'notApplicable';

interface MetaAttributionPlugin {
  getAnonymousId(): Promise<{ anonymousId: string | null }>;
  requestTrackingAuthorization(): Promise<{ status: TrackingAuthorizationStatus }>;
  getTrackingAuthorizationStatus(): Promise<{ status: TrackingAuthorizationStatus }>;
}

const asError = (e: unknown): Error => (e instanceof Error ? e : new Error(String(e)));

let MetaAttribution: MetaAttributionPlugin | null = null;

if (Capacitor.isNativePlatform()) {
  MetaAttribution = registerPlugin<MetaAttributionPlugin>('MetaAttribution');
}

/**
 * Reads the Facebook SDK's anonymous ID and stores it on the RevenueCat
 * customer as $fbAnonId. Must run after Purchases.configure(). Resolves
 * null when the Facebook SDK is not configured on this build (no client
 * token yet), in which case nothing is written.
 */
export const syncFacebookAnonymousId = async (): Promise<string | null> => {
  if (!MetaAttribution) return null;
  try {
    const { anonymousId } = await MetaAttribution.getAnonymousId();
    if (!anonymousId) return null;
    await Purchases.setFBAnonymousID({ fbAnonymousID: anonymousId });
    return anonymousId;
  } catch (e) {
    captureError(asError(e), {}, ['meta-attribution', 'fb-anonymous-id']);
    return null;
  }
};

/**
 * Shows the iOS App Tracking Transparency prompt (once, iOS handles the
 * "only ask once" rule) and then asks RevenueCat to re-read the device
 * identifiers so $idfa and $attConsentStatus reflect the answer. On Android
 * there is no prompt and this resolves immediately.
 */
export const requestTrackingConsent = async (): Promise<TrackingAuthorizationStatus> => {
  if (!MetaAttribution) return 'notApplicable';
  try {
    const { status } = await MetaAttribution.requestTrackingAuthorization();
    if (status !== 'notApplicable') {
      // collectDeviceIdentifiers() already ran at configure time, but that
      // was before the prompt, when the IDFA was all zeros.
      await Purchases.collectDeviceIdentifiers();
    }
    return status;
  } catch (e) {
    captureError(asError(e), {}, ['meta-attribution', 'att-prompt']);
    return 'notDetermined';
  }
};

// The Adjust attribution re-read needs app.tsx's saveAttribution closure
// (it writes to RevenueCat and Mixpanel). app.tsx registers it at boot so the
// prompt can be triggered from screens that do not own that closure.
type AttributionRefresh = () => Promise<unknown> | unknown;
let attributionRefresh: AttributionRefresh | null = null;

export const registerAttributionRefresh = (fn: AttributionRefresh): void => {
  attributionRefresh = fn;
};

let trackingConsentStarted = false;

/**
 * The App Tracking Transparency chain that used to run from the boot
 * sequence: prompt, Facebook anonymous ID, a short wait for Adjust to
 * settle, then an attribution re-read (consent can turn "No User Consent"
 * into the real network and campaign). Called when the user leaves the first
 * onboarding screen and again on the first authed dashboard mount, so people
 * who sign straight in are still asked once. Module-level once guard here;
 * iOS itself only ever shows the prompt once. Native only. Fire and forget.
 */
export const runTrackingConsentOnce = (): void => {
  if (!MetaAttribution || trackingConsentStarted) return;
  trackingConsentStarted = true;
  requestTrackingConsent()
    // Facebook anonymous ID -> RevenueCat $fbAnonId. Without it RevenueCat
    // never forwards App Store trials/purchases to Meta Ads (2026-09-03).
    .then(() => syncFacebookAnonymousId())
    .then(() => new Promise((resolve) => { setTimeout(resolve, 5000); }))
    .then(() => (attributionRefresh ? attributionRefresh() : undefined))
    .catch(() => undefined);
};
